import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSeo } from '@/hooks/useSeo';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import { faqs } from '@/mocks/faqs';
import type { FaqItem } from '@/types/content';

export default function FaqPage() {
  const items = faqs as FaqItem[];
  const [openId, setOpenId] = useState<string>(items[0]?.id ?? '');

  useSeo({
    title: '常见问题 | 美日吃瓜 内容来源与核对说明',
    description:
      '关于美日吃瓜的爆料来源、事实核对流程、稿件更新机制与匿名线索投递方式，这里集中回答了读者最常提出的问题。',
    keywords: '美日吃瓜常见问题,爆料来源,事实核对,匿名投递,稿件更正',
    path: '/faq',
    jsonLd: {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'FAQPage',
          mainEntity: items.map((item) => ({
            '@type': 'Question',
            name: item.question,
            acceptedAnswer: { '@type': 'Answer', text: item.answer },
          })),
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: '/' },
            { '@type': 'ListItem', position: 2, name: '常见问题', item: '/faq' },
          ],
        },
      ],
    },
  });

  return (
    <>
      <Breadcrumbs items={[{ label: '常见问题' }]} />

      <section className="border-b border-background-300/70 bg-background-100">
        <div className="mx-auto max-w-6xl px-4 py-10 md:px-6 md:py-14">
          <p className="mb-3 font-label text-xs uppercase tracking-[0.32em] text-primary-600">
            FAQ
          </p>
          <h1 className="max-w-3xl font-heading text-2xl font-bold leading-tight text-foreground-950 md:text-4xl">
            常见问题：关于来源、核对与线索投递
          </h1>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            我们希望读者不仅读到结论，也清楚这些结论是怎么来的。以下问题覆盖了内容生产流程中最常被询问的环节，如果仍有疑问，欢迎直接来信。
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-6xl px-4 py-10 md:px-6 md:py-14">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <div className="divide-y divide-background-300/70 overflow-hidden rounded-lg border border-background-300/70 bg-background-100">
              {items.map((item, index) => {
                const open = openId === item.id;
                return (
                  <div key={item.id}>
                    <h2>
                      <button
                        type="button"
                        onClick={() => setOpenId(open ? '' : item.id)}
                        aria-expanded={open}
                        className="flex w-full cursor-pointer items-start justify-between gap-4 px-5 py-5 text-left transition-colors hover:bg-background-200/60"
                      >
                        <span className="flex gap-3">
                          <span className="font-label text-xs text-primary-600">
                            {String(index + 1).padStart(2, '0')}
                          </span>
                          <span className="font-heading text-sm font-semibold leading-snug text-foreground-900 md:text-base">
                            {item.question}
                          </span>
                        </span>
                        <span className="flex h-5 w-5 shrink-0 items-center justify-center text-primary-600">
                          <i className={open ? 'ri-subtract-line' : 'ri-add-line'} />
                        </span>
                      </button>
                    </h2>
                    {open ? (
                      <div className="px-5 pb-5 pl-11 text-sm leading-loose text-foreground-600">
                        {item.answer}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>

          <aside className="flex flex-col gap-5">
            <div className="rounded-lg border border-background-300/70 bg-background-200/60 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">
                没有找到你的问题
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                把问题直接发给编辑部，我们会在两个工作日内回复。涉及具体稿件的指正，请附上可佐证的材料。
              </p>
              <Link
                to="/contact"
                className="mt-4 inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-600 px-5 py-2.5 font-label text-sm font-medium text-background-50 transition-colors hover:bg-primary-700"
              >
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-mail-send-line" />
                </span>
                联系我们
              </Link>
            </div>

            <div className="rounded-lg border border-background-300/70 bg-background-100 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">快速跳转</h2>
              <ul className="mt-3 space-y-2.5">
                <li>
                  <Link
                    to="/about"
                    className="flex items-center gap-2 text-sm text-foreground-700 transition-colors hover:text-primary-600"
                  >
                    <span className="flex h-4 w-4 items-center justify-center text-primary-600">
                      <i className="ri-arrow-right-s-line" />
                    </span>
                    了解美日吃瓜的定位
                  </Link>
                </li>
                <li>
                  <Link
                    to="/category/corruption"
                    className="flex items-center gap-2 text-sm text-foreground-700 transition-colors hover:text-primary-600"
                  >
                    <span className="flex h-4 w-4 items-center justify-center text-primary-600">
                      <i className="ri-arrow-right-s-line" />
                    </span>
                    浏览官员贪腐档案
                  </Link>
                </li>
                <li>
                  <Link
                    to="/search?q=时间线"
                    className="flex items-center gap-2 text-sm text-foreground-700 transition-colors hover:text-primary-600"
                  >
                    <span className="flex h-4 w-4 items-center justify-center text-primary-600">
                      <i className="ri-arrow-right-s-line" />
                    </span>
                    使用站内检索
                  </Link>
                </li>
              </ul>
            </div>
          </aside>
        </div>
      </div>
    </>
  );
}